import React, { useState } from 'react'
import { Card, makeStyles } from '@material-ui/core'
import FileCopyIcon from '@material-ui/icons/FileCopyOutlined'
import LaunchIcon from '@material-ui/icons/Launch'
import { addAuth0Prefix, getActivityColor } from '@paintscout/util'
import StarIcon from '@material-ui/icons/Star'
import Table, { TableBody, TableCell, TableRow } from '@paintscout/ui/components/Table'
import Typography from '@paintscout/ui/components/Typography'
import Tooltip from '@paintscout/ui/components/Tooltip'
import IconButton from '@paintscout/ui/components/IconButton'
import HtmlContent from '@paintscout/ui/components/HtmlContent'
import EstimatorAvatar from '../EstimatorAvatar'
import type { Details } from '../DetailsContainer/DetailsContainer'

const useStyles = makeStyles((theme) => ({
  root: {
    boxShadow: theme.shadows[2]
  },
  detailRow: {
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  detailCell: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flex: 1
  },
  iconCell: {
    minWidth: 40,
    textAlign: 'center',
    '& button': {
      color: theme.paintscout.palette.brand.Neutral.P85
    }
  },
  preferredStar: {
    color: '#f5a622',
    fontSize: '1rem',
    marginBottom: theme.spacing(0.25),
    marginLeft: theme.spacing(0.5)
  },
  innerHtml: {
    '& p': {
      margin: 0
    },
    '& ul': {
      margin: 0,
      paddingLeft: '1.5rem'
    }
  }
}))

export interface MobileDetailsTableProps {
  details: Details[]
}

export default function MobileDetailsTable({ details }: MobileDetailsTableProps) {
  const classes = useStyles()
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null)
  const handleCopy = (value: string, index: number) => {
    navigator.clipboard.writeText(value)
    setCopiedIndex(index)
    setTimeout(() => setCopiedIndex(null), 1000)
  }

  const handleLink = (detail: Details) => {
    if (detail.link) {
      detail.link()
    }
  }

  return (
    <Card className={classes.root} elevation={0}>
      <Table>
        <TableBody>
          {details.map((detail, idx) => (
            <TableRow key={idx} className={classes.detailRow} hover={!!detail.link} onClick={() => handleLink(detail)}>
              <TableCell className={classes.detailCell}>
                <Typography variant="subtitle2" style={{ display: 'flex', alignItems: 'center' }}>
                  {detail.title} {detail.icon}{' '}
                  {!detail?.icon && detail?.star && <StarIcon className={classes.preferredStar} />}
                </Typography>
                {detail.html ? (
                  <HtmlContent className={classes.innerHtml} content={String(detail.value)} />
                ) : (
                  <Typography variant="body1" color="textPrimary">
                    {detail.value}
                  </Typography>
                )}
              </TableCell>
              <TableCell className={classes.iconCell}>
                {detail.canCopy && detail.value !== '-' && (
                  <Tooltip open={copiedIndex === idx} content="Copied!" placement="left" hideIcon>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleCopy(detail.value as string, idx)
                      }}
                    >
                      <FileCopyIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {!detail.canCopy && detail.link && (
                  <IconButton size="small" onClick={detail.link}>
                    <LaunchIcon />
                  </IconButton>
                )}
                {!detail.canCopy && !detail.link && detail.user && (
                  <div style={{ marginTop: '4px' }}>
                    <EstimatorAvatar
                      user={{
                        id: detail.user?.user_id,
                        firstName: detail.user?.user_metadata?.firstName,
                        lastName: detail.user?.user_metadata?.lastName
                      }}
                      userPictures={{
                        [addAuth0Prefix(detail.user?.user_id)]: detail.user?.user_metadata?.customAvatar?.cdnValue ?? ''
                      }}
                      avatarColor={getActivityColor(detail.user?.user_metadata?.color)}
                    />
                  </div>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  )
}
